import { quizQuestions } from './quizData'

// ── Types ──────────────────────────────────────────────
export interface SessionStep {
  step: string
  timestamp: number
  data?: Record<string, unknown>
}

export interface QuizSession {
  id: string
  visitorId: string
  startedAt: number
  device: 'mobile' | 'desktop'
  steps: SessionStep[]
  completed: boolean
  resultId: string | null
  ctaClicked: boolean
}

const STORAGE_KEY = 'quiz_analytics'
const VISITOR_KEY = 'quiz_visitor_id'

// ── Helpers ────────────────────────────────────────────
function generateId(): string {
  return Math.random().toString(36).substring(2, 10) + Date.now().toString(36)
}

function getDeviceType(): 'mobile' | 'desktop' {
  if (typeof window === 'undefined') return 'desktop'
  return window.innerWidth < 768 ? 'mobile' : 'desktop'
}

function readSessions(): QuizSession[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    return raw ? JSON.parse(raw) : []
  } catch {
    return []
  }
}

function writeSessions(sessions: QuizSession[]): void {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(sessions))
}

// ── Visitor ID (unique per browser) ────────────────────
export function getVisitorId(): string {
  let id = localStorage.getItem(VISITOR_KEY)
  if (!id) {
    id = generateId() + '-' + generateId()
    localStorage.setItem(VISITOR_KEY, id)
  }
  return id
}

// ── Find existing session for this visitor ─────────────
function findSessionByVisitor(visitorId: string): QuizSession | null {
  const sessions = readSessions()
  return sessions.find((s) => s.visitorId === visitorId) || null
}

// ── Public API ─────────────────────────────────────────

/**
 * Get or create a session for the current visitor.
 * Returns the session ID. If the visitor already has a session,
 * returns the existing one (no duplicate).
 */
export function getOrCreateSession(): string {
  const visitorId = getVisitorId()
  const existing = findSessionByVisitor(visitorId)

  if (existing) {
    return existing.id
  }

  const session: QuizSession = {
    id: generateId(),
    visitorId,
    startedAt: Date.now(),
    device: getDeviceType(),
    steps: [{ step: 'intro', timestamp: Date.now() }],
    completed: false,
    resultId: null,
    ctaClicked: false,
  }
  const sessions = readSessions()
  sessions.push(session)
  writeSessions(sessions)
  return session.id
}

/**
 * Track a step for a session. Prevents duplicate steps
 * (same step name won't be recorded twice for the same session).
 */
export function trackStep(
  sessionId: string,
  step: string,
  data?: Record<string, unknown>,
): void {
  const sessions = readSessions()
  const idx = sessions.findIndex((s) => s.id === sessionId)
  if (idx === -1) return

  const session = sessions[idx]

  // Prevent duplicate step tracking (except question steps which have unique names)
  const alreadyTracked = session.steps.some((s) => s.step === step)
  if (alreadyTracked && !step.startsWith('question_')) return
  if (alreadyTracked && step.startsWith('question_')) return

  sessions[idx] = {
    ...session,
    steps: [...session.steps, { step, timestamp: Date.now(), data }],
  }
  writeSessions(sessions)
}

export function completeSession(sessionId: string, resultId: string): void {
  const sessions = readSessions()
  const idx = sessions.findIndex((s) => s.id === sessionId)
  if (idx === -1) return

  const session = sessions[idx]
  if (session.completed) return // Already completed, don't double-count

  sessions[idx] = {
    ...session,
    completed: true,
    resultId,
    steps: [
      ...session.steps,
      { step: 'result_viewed', timestamp: Date.now(), data: { resultId } },
    ],
  }
  writeSessions(sessions)
}

export function trackCTAClick(sessionId: string): void {
  const sessions = readSessions()
  const idx = sessions.findIndex((s) => s.id === sessionId)
  if (idx === -1) return

  const session = sessions[idx]
  if (session.ctaClicked) return // Already clicked, don't double-count

  sessions[idx] = {
    ...session,
    ctaClicked: true,
    steps: [...session.steps, { step: 'cta_clicked', timestamp: Date.now() }],
  }
  writeSessions(sessions)
}

export function getAllSessions(): QuizSession[] {
  return readSessions()
}

export function clearSessions(): void {
  localStorage.removeItem(STORAGE_KEY)
  // Also clear visitor IDs so next visit creates fresh sessions
  localStorage.removeItem(VISITOR_KEY)
}

// ── Computed Analytics ──────────────────────────────────
export interface FunnelStep {
  name: string
  label: string
  count: number
  ctr: number
  percentOfTotal: number
}

export interface AnalyticsSummary {
  totalSessions: number
  completionRate: number
  ctaClickRate: number
  avgQuestionsAnswered: number
  biggestDropStep: string
  biggestDropPercent: number
  mobileCount: number
  desktopCount: number
  funnel: FunnelStep[]
  resultDistribution: { id: string; count: number; percent: number }[]
  questionAnswers: Record<
    number,
    { tag: string; text: string; count: number; percent: number }[]
  >
}

export function computeAnalytics(sessions: QuizSession[]): AnalyticsSummary {
  const total = sessions.length
  if (total === 0) {
    return {
      totalSessions: 0,
      completionRate: 0,
      ctaClickRate: 0,
      avgQuestionsAnswered: 0,
      biggestDropStep: '-',
      biggestDropPercent: 0,
      mobileCount: 0,
      desktopCount: 0,
      funnel: [],
      resultDistribution: [],
      questionAnswers: {},
    }
  }

  const funnelStepNames = [
    { name: 'intro', label: 'Intro' },
    ...Array.from({ length: 12 }, (_, i) => ({
      name: `question_${i + 1}`,
      label: `Q${i + 1}`,
    })),
    { name: 'loading', label: 'Loading' },
    { name: 'result_viewed', label: 'Result' },
    { name: 'cta_clicked', label: 'CTA Click' },
  ]

  const stepCounts: Record<string, number> = {}
  for (const s of funnelStepNames) {
    stepCounts[s.name] = 0
  }

  for (const session of sessions) {
    const sessionStepNames = new Set(session.steps.map((s) => s.step))
    for (const s of funnelStepNames) {
      if (sessionStepNames.has(s.name)) {
        stepCounts[s.name]++
      }
    }
  }

  const funnel: FunnelStep[] = funnelStepNames.map((s, idx) => {
    const count = stepCounts[s.name]
    const prev = idx > 0 ? stepCounts[funnelStepNames[idx - 1].name] : total
    return {
      name: s.name,
      label: s.label,
      count,
      ctr: prev > 0 ? (count / prev) * 100 : 0,
      percentOfTotal: total > 0 ? (count / total) * 100 : 0,
    }
  })

  let biggestDrop = 0
  let biggestDropStep = '-'
  for (let i = 1; i < funnel.length; i++) {
    const drop = funnel[i - 1].count - funnel[i].count
    const dropPct =
      funnel[i - 1].count > 0 ? (drop / funnel[i - 1].count) * 100 : 0
    if (dropPct > biggestDrop) {
      biggestDrop = dropPct
      biggestDropStep = funnel[i].label
    }
  }

  const completed = sessions.filter((s) => s.completed).length
  const ctaClicked = sessions.filter((s) => s.ctaClicked).length
  const completionRate = (completed / total) * 100
  const ctaClickRate = completed > 0 ? (ctaClicked / completed) * 100 : 0

  let totalQs = 0
  for (const session of sessions) {
    const qs = session.steps.filter((s) =>
      s.step.startsWith('question_'),
    ).length
    totalQs += qs
  }
  const avgQuestionsAnswered = totalQs / total

  const mobileCount = sessions.filter((s) => s.device === 'mobile').length
  const desktopCount = sessions.filter((s) => s.device === 'desktop').length

  const resultCounts: Record<string, number> = { A: 0, B: 0, C: 0, D: 0 }
  for (const session of sessions) {
    if (session.resultId && resultCounts[session.resultId] !== undefined) {
      resultCounts[session.resultId]++
    }
  }
  const totalResults = Object.values(resultCounts).reduce((a, b) => a + b, 0)
  const resultDistribution = Object.entries(resultCounts).map(
    ([id, count]) => ({
      id,
      count,
      percent: totalResults > 0 ? (count / totalResults) * 100 : 0,
    }),
  )

  const questionAnswers: Record<
    number,
    { tag: string; text: string; count: number; percent: number }[]
  > = {}
  for (let q = 0; q < 12; q++) {
    const qId = q + 1
    const stepName = `question_${qId}`
    const answerCounts: Record<string, number> = {}
    let totalForQ = 0

    for (const session of sessions) {
      const step = session.steps.find((s) => s.step === stepName)
      if (step?.data?.tag) {
        const tag = step.data.tag as string
        answerCounts[tag] = (answerCounts[tag] || 0) + 1
        totalForQ++
      }
    }

    const question = quizQuestions[q]
    questionAnswers[qId] = question.options.map((opt) => ({
      tag: opt.tag,
      text: opt.text,
      count: answerCounts[opt.tag] || 0,
      percent:
        totalForQ > 0 ? ((answerCounts[opt.tag] || 0) / totalForQ) * 100 : 0,
    }))
  }

  return {
    totalSessions: total,
    completionRate,
    ctaClickRate,
    avgQuestionsAnswered,
    biggestDropStep,
    biggestDropPercent: biggestDrop,
    mobileCount,
    desktopCount,
    funnel,
    resultDistribution,
    questionAnswers,
  }
}
